import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./PersonListFacets.module.styl";

const EXCLUSION_TAGS = Object.freeze({
  exclude: {
    label: t("filter.exclude"),
    aliases: ["exclude", "excluded", "ignore"]
  },
  duplicate: {
    label: t("voter.duplicate"),
    aliases: ["duplicate", "duplicated"]
  },
  deceased: {
    label: t("filter.deceased"),
    aliases: ["deceased"]
  }
});

const EXCLUSION_TAG_STATES = Object.freeze([
  { value: "exclude", label: t("filter.hide") },
  { value: "include", label: t("filter.include") },
  { value: "only", label: t("filter.only") }
]);

const DEFAULT_EXCLUSION_TAG_FILTERS = Object.freeze(
  Object.fromEntries(
    Object.keys(EXCLUSION_TAGS).map((key) => [key, "exclude"])
  )
);

export const DEFAULT_PERSON_LIST_FILTERS = Object.freeze({
  sexCode: [],
  hasPhoto: "all",
  hasIdentifier: "all",
  tags: [],
  exclusionTags: DEFAULT_EXCLUSION_TAG_FILTERS
});

const SEX_LABELS = Object.freeze({
  "0": t("common.unknown"),
  "1": t("filter.male"),
  "2": t("filter.female")
});

const YES_NO_OPTIONS = [
  { value: "all", label: t("common.all") },
  { value: "yes", label: t("common.yes") },
  { value: "no", label: t("common.no") }
];

const normalizeArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean);
};

const normalizeChoice = (value, allowed, fallback) => {
  return allowed.includes(value) ? value : fallback;
};

const getAllExclusionTagKeys = () => {
  const out = new Set();

  for (const config of Object.values(EXCLUSION_TAGS)) {
    for (const alias of config.aliases || []) {
      out.add(alias);
    }
  }

  return out;
};

const EXCLUSION_TAG_KEY_SET = getAllExclusionTagKeys();

const isExclusionTag = (tag) => {
  return EXCLUSION_TAG_KEY_SET.has(String(tag || "").trim());
};

const normalizeExclusionTagFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  const out = {};

  for (const key of Object.keys(EXCLUSION_TAGS)) {
    out[key] = normalizeChoice(
      input[key],
      ["exclude", "include", "only"],
      DEFAULT_EXCLUSION_TAG_FILTERS[key]
    );
  }

  return out;
};

export const normalizePersonListFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  return {
    sexCode: normalizeArray(input.sexCode || input.sex),
    hasPhoto: normalizeChoice(
      input.hasPhoto,
      ["all", "yes", "no"],
      DEFAULT_PERSON_LIST_FILTERS.hasPhoto
    ),
    hasIdentifier: normalizeChoice(
      input.hasIdentifier,
      ["all", "yes", "no"],
      DEFAULT_PERSON_LIST_FILTERS.hasIdentifier
    ),
    tags: normalizeArray(input.tags).filter((tag) => !isExclusionTag(tag)),
    exclusionTags: normalizeExclusionTagFilters(input.exclusionTags)
  };
};

export const isDefaultPersonListFilters = (value) => {
  const filters = normalizePersonListFilters(value);

  const exclusionDefaults = Object.keys(EXCLUSION_TAGS).every(
    (key) => filters.exclusionTags[key] === DEFAULT_EXCLUSION_TAG_FILTERS[key]
  );

  return filters.sexCode.length === 0 &&
    filters.hasPhoto === "all" &&
    filters.hasIdentifier === "all" &&
    filters.tags.length === 0 &&
    exclusionDefaults;
};

export const getPersonListActiveFilterCount = (value) => {
  const filters = normalizePersonListFilters(value);
  let count = 0;

  count += filters.sexCode.length;
  if (filters.hasPhoto !== "all") count += 1;
  if (filters.hasIdentifier !== "all") count += 1;
  count += filters.tags.length;

  for (const key of Object.keys(EXCLUSION_TAGS)) {
    if (filters.exclusionTags[key] !== DEFAULT_EXCLUSION_TAG_FILTERS[key]) {
      count += 1;
    }
  }

  return count;
};

const escapeRsqlString = (value) => {
  return String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');
};

const quoteRsql = (value) => `"${escapeRsqlString(value)}"`;

const rsqlOr = (parts) => {
  const clean = parts.filter(Boolean);
  if (!clean.length) return "";
  if (clean.length === 1) return clean[0];
  return `(${clean.join(",")})`;
};

const rsqlAnd = (parts) => {
  const clean = parts.filter(Boolean);
  if (!clean.length) return "";
  if (clean.length === 1) return clean[0];
  return clean.join(";");
};

const buildTagHasRsql = (tag) => {
  return `tags=has=${quoteRsql(tag)}`;
};

const buildTagNotHasRsql = (tag) => {
  return `tags=nhas=${quoteRsql(tag)}`;
};

const buildTagGroupHasRsql = (aliases) => {
  return rsqlOr((aliases || []).map(buildTagHasRsql));
};

const buildTagGroupNotHasRsql = (aliases) => {
  return rsqlAnd((aliases || []).map(buildTagNotHasRsql));
};

const buildTernaryPresenceRsql = (field, value) => {
  if (value === "yes") {
    return rsqlAnd([
      `${field}=notnull=true`,
      `${field}=nre="^$"`
    ]);
  }

  if (value === "no") {
    return rsqlOr([
      `${field}=isnull=true`,
      `${field}==""`
    ]);
  }

  return "";
};

const buildNullablePresenceRsql = (field, value) => {
  if (value === "yes") {
    return `${field}=notnull=true`;
  }

  if (value === "no") {
    return `${field}=isnull=true`;
  }

  return "";
};

export const buildPersonFilterRsql = (value) => {
  const filters = normalizePersonListFilters(value);
  const parts = [];

  if (filters.sexCode.length) {
    parts.push(`sexCode=in=(${filters.sexCode.map(quoteRsql).join(",")})`);
  }


  parts.push(buildNullablePresenceRsql("defaultImageId", filters.hasPhoto));
  parts.push(buildTernaryPresenceRsql("nationalIdentifier", filters.hasIdentifier));

  if (filters.tags.length) {
    parts.push(rsqlOr(filters.tags.map((tag) => buildTagHasRsql(tag))));
  }

  for (const [key, state] of Object.entries(filters.exclusionTags)) {
    const config = EXCLUSION_TAGS[key];

    if (!config) {
      continue;
    }

    if (state === "exclude") {
      parts.push(buildTagGroupNotHasRsql(config.aliases));
    }

    if (state === "only") {
      parts.push(buildTagGroupHasRsql(config.aliases));
    }
  }

  return rsqlAnd(parts);
};

export const combinePersonListRsql = (...queries) => {
  return rsqlAnd(queries.map((query) => String(query || "").trim()).filter(Boolean));
};

const emptyFacetCounts = Object.freeze({
  total: 0,
  profile: {
    hasPhoto: {},
    hasIdentifier: {}
  },
  sexCode: [],
  tags: []
});

const buildFacetRsqlQuery = ({ search, filters }) => {
  const searchValue = String(search || "").trim();

  const searchQuery = searchValue
    ? ["firstName", "lastName", "nationalIdentifier", "officeIdentifierNumber", "recordNumber"]
      .map((field) => `${field}=re="(?i).*${escapeRsqlString(searchValue)}.*"`)
      .join(",")
    : "";

  return combinePersonListRsql(
    searchQuery,
    buildPersonFilterRsql(filters)
  );
};

export const loadPersonFacetCounts = async ({ search, filters }) => {
  try {
    const query = buildFacetRsqlQuery({ search, filters });

    const counts = await rest.request(
      getServiceUri() + "person/facets",
      "POST",
      {
        columns: [],
        query,
        limit: null,
        offset: null,
        sort: []
      }
    );

    return {
      ...emptyFacetCounts,
      ...(counts || {}),
      profile: {
        ...emptyFacetCounts.profile,
        ...(counts?.profile || {})
      }
    };
  } catch (error) {
    console.warn("person/facets is unavailable. Facet counts will be hidden.", error);
    return emptyFacetCounts;
  }
};

const countFor = (counts, path, fallback = 0) => {
  let current = counts || {};

  for (const key of path) {
    if (!current || typeof current !== "object") {
      return fallback;
    }

    current = current[key];
  }

  const number = Number(current);
  return Number.isFinite(number) ? number : fallback;
};

const listCounts = (counts, key) => {
  const values = counts?.[key];
  return Array.isArray(values) ? values : [];
};

const filteredTagCounts = (counts) => {
  return listCounts(counts, "tags").filter((item) => !isExclusionTag(item.value));
};

const countForTagAliases = (counts, aliases) => {
  const rows = listCounts(counts, "tags");

  return rows
    .filter((item) => (aliases || []).includes(item.value))
    .reduce((sum, item) => sum + Number(item.count || 0), 0);
};

const toggleArrayValue = (values, value) => {
  const current = normalizeArray(values);

  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
};

const labelForSexCode = (value) => {
  return SEX_LABELS[String(value)] || String(value);
};

const FacetSection = ({ title, children }) => {
  return (
    <section className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
};

const RadioOption = ({ label, selected, count, disabled, onClick }) => {
  return (
    <button
      type="button"
      className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option}
      disabled={disabled}
      onClick={onClick}
    >
      <span className={styles.radioDot} />
      <span className={styles.optionLabel}>{label}</span>
      {count !== undefined ? <span className={styles.count}>{count}</span> : null}
    </button>
  );
};

const CheckboxOption = ({ label, selected, count, disabled, onClick }) => {
  return (
    <button
      type="button"
      className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option}
      disabled={disabled}
      onClick={onClick}
    >
      <span className={styles.checkBox}>{selected ? "✓" : ""}</span>
      <span className={styles.optionLabel}>{label}</span>
      {count !== undefined ? <span className={styles.count}>{count}</span> : null}
    </button>
  );
};

const Chip = ({ label, onRemove }) => {
  return (
    <button type="button" className={styles.chip} onClick={onRemove}>
      <span>{label}</span>
      <span className={styles.chipClose}>×</span>
    </button>
  );
};

const setExclusionTagState = (filters, key, state) => {
  return {
    exclusionTags: {
      ...filters.exclusionTags,
      [key]: state
    }
  };
};

export const PersonListFacetButton = ({ controller }) => {
  const activeCount = controller?.activeCount || 0;

  return (
    <button
      type="button"
      className={activeCount ? `${styles.filterButton} ${styles.filterButtonActive}` : styles.filterButton}
      onClick={controller.toggleDrawer}
    >
      <span className={styles.filterButtonIcon}>
        <TuneIcon fontSize="inherit" />
      </span>
      <span className={styles.filterButtonLabel}>{t("common.filters")}</span>
      {activeCount ? <span className={styles.filterButtonBadge}>{activeCount}</span> : null}
    </button>
  );
};

export const PersonListFacetChips = ({ controller }) => {
  const filters = normalizePersonListFilters(controller?.value);

  if (isDefaultPersonListFilters(filters)) {
    return null;
  }

  const set = (patch) => controller.patchValue(patch);

  return (
    <div className={styles.chipsBar}>
      {filters.sexCode.map((value) => (
        <Chip
          key={`sex-${value}`}
          label={`${t("person.sex")}: ${labelForSexCode(value)}`}
          onRemove={() => set({ sexCode: filters.sexCode.filter((item) => item !== value) })}
        />
      ))}

      {filters.hasPhoto !== "all" ? (
        <Chip
          label={`${t("common.photo")}: ${filters.hasPhoto === "yes" ? t("common.yes") : t("common.no")}`}
          onRemove={() => set({ hasPhoto: "all" })}
        />
      ) : null}

      {filters.hasIdentifier !== "all" ? (
        <Chip
          label={`${t("common.identifier")}: ${filters.hasIdentifier === "yes" ? t("common.yes") : t("common.no")}`}
          onRemove={() => set({ hasIdentifier: "all" })}
        />
      ) : null}

      {filters.tags.map((value) => (
        <Chip
          key={`tag-${value}`}
          label={`${t("geo.tag")}: ${value}`}
          onRemove={() => set({ tags: filters.tags.filter((item) => item !== value) })}
        />
      ))}

      {Object.keys(EXCLUSION_TAGS).map((key) => {
        const state = filters.exclusionTags[key];

        if (state === DEFAULT_EXCLUSION_TAG_FILTERS[key]) {
          return null;
        }

        const label = EXCLUSION_TAGS[key].label;
        const stateLabel = EXCLUSION_TAG_STATES.find((item) => item.value === state)?.label || state;

        return (
          <Chip
            key={`exclusion-${key}`}
            label={`${label}: ${stateLabel}`}
            onRemove={() => set(setExclusionTagState(filters, key, DEFAULT_EXCLUSION_TAG_FILTERS[key]))}
          />
        );
      })}

      <button type="button" className={styles.clearAll} onClick={controller.reset}>
        {t("common.clearAll")}
      </button>
    </div>
  );
};

const ExclusionTagFilter = ({ filters, counts, tagKey, onSet }) => {
  const config = EXCLUSION_TAGS[tagKey];
  const selected = filters.exclusionTags[tagKey];
  const count = countForTagAliases(counts, config.aliases);

  return (
    <div className={styles.exclusionCompactRow}>
      <div className={styles.exclusionCompactMeta}>
        <span className={styles.exclusionCompactName}>{config.label}</span>
        <span className={styles.exclusionCompactCount}>{count}</span>
      </div>

      <div className={styles.segmentedControl}>
        {EXCLUSION_TAG_STATES.map((state) => (
          <button
            key={`${tagKey}-${state.value}`}
            type="button"
            className={
              selected === state.value
                ? `${styles.segmentedButton} ${styles.segmentedButtonSelected}`
                : styles.segmentedButton
            }
            onClick={() => onSet(tagKey, state.value)}
          >
            {state.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const ExclusionsSection = ({ filters, counts, onSet }) => {
  const [open, setOpen] = React.useState(false);

  const changedCount = Object.keys(EXCLUSION_TAGS).filter(
    (key) => filters.exclusionTags[key] !== DEFAULT_EXCLUSION_TAG_FILTERS[key]
  ).length;

  return (
    <section className={styles.exclusionCollapsedSection}>
      <button
        type="button"
        className={styles.exclusionCollapseButton}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.exclusionCollapseText}>{t("filter.exclusions")}</span>

        {changedCount ? (
          <span className={styles.exclusionChangedBadge}>{changedCount}</span>
        ) : null}

        <span className={open ? `${styles.exclusionChevron} ${styles.exclusionChevronOpen}` : styles.exclusionChevron}>
          <ExpandMoreIcon fontSize="inherit" />
        </span>
      </button>

      {open ? (
        <div className={styles.exclusionCollapsedBody}>
          <div className={styles.exclusionCompactHelp}>
            {t("filter.exclusionHelp")}
          </div>

          {Object.keys(EXCLUSION_TAGS).map((tagKey) => (
            <ExclusionTagFilter
              key={tagKey}
              filters={filters}
              counts={counts}
              tagKey={tagKey}
              onSet={onSet}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};

const PersonListFacets = ({ controller }) => {
  const filters = normalizePersonListFilters(controller?.value);
  const counts = controller?.counts || {};

  const set = (patch) => controller.patchValue(patch);

  const setExclusion = (key, state) => {
    set(setExclusionTagState(filters, key, state));
  };

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div>
          <div className={styles.drawerTitle}>{t("common.filters")}</div>
          <div className={styles.drawerSubtitle}>
            {controller.countsLoading ? t("common.updatingCounts") : t("filter.refinePeople")}
          </div>
        </div>

        <button type="button" className={styles.closeButton} onClick={controller.closeDrawer} aria-label={t("common.closeFilters")}>
          <CloseIcon fontSize="inherit" />
        </button>
      </div>

      <div className={styles.drawerBody}>
        <FacetSection title={t("common.photo")}>
          {YES_NO_OPTIONS.map((option) => (
            <RadioOption
              key={`photo-${option.value}`}
              label={option.label}
              selected={filters.hasPhoto === option.value}
              count={option.value === "all" ? countFor(counts, ["total"]) : countFor(counts, ["profile", "hasPhoto", option.value])}
              onClick={() => set({ hasPhoto: option.value })}
            />
          ))}
        </FacetSection>

        <FacetSection title={t("common.identifier")}>
          {YES_NO_OPTIONS.map((option) => (
            <RadioOption
              key={`identifier-${option.value}`}
              label={option.label}
              selected={filters.hasIdentifier === option.value}
              count={option.value === "all" ? countFor(counts, ["total"]) : countFor(counts, ["profile", "hasIdentifier", option.value])}
              onClick={() => set({ hasIdentifier: option.value })}
            />
          ))}
        </FacetSection>

        <FacetSection title={t("person.sex")}>
          {listCounts(counts, "sexCode").length ? (
            listCounts(counts, "sexCode").map((item) => (
              <CheckboxOption
                key={item.value}
                label={labelForSexCode(item.value)}
                selected={filters.sexCode.includes(String(item.value))}
                count={item.count}
                onClick={() => set({ sexCode: toggleArrayValue(filters.sexCode, String(item.value)) })}
              />
            ))
          ) : (
            <div className={styles.emptyHint}>{t("filter.noSexValues")}</div>
          )}
        </FacetSection>

        <FacetSection title={t("common.tags")}>
          {filteredTagCounts(counts).length ? (
            filteredTagCounts(counts).map((item) => (
              <CheckboxOption
                key={item.value}
                label={item.value}
                selected={filters.tags.includes(String(item.value))}
                count={item.count}
                onClick={() => set({ tags: toggleArrayValue(filters.tags, String(item.value)) })}
              />
            ))
          ) : (
            <div className={styles.emptyHint}>{t("filter.noTagValues")}</div>
          )}
        </FacetSection>

        <ExclusionsSection
          filters={filters}
          counts={counts}
          onSet={setExclusion}
        />
      </div>

      <div className={styles.drawerFooter}>
        <button
          type="button"
          className={styles.secondaryAction}
          onClick={controller.reset}
          disabled={controller.activeCount === 0}
        >
          <RestartAltIcon fontSize="inherit" />
          <span>{t("common.resetFilters")}</span>
        </button>

        <button
          type="button"
          className={styles.primaryAction}
          onClick={controller.closeDrawer}
        >
          {t("common.done")}
        </button>
      </div>
    </div>
  );
};

export const PERSON_LIST_FACETS = {
  storageKey: "haiti.person.v3",
  overlayLabel: t("filter.personFilters"),
  defaultValue: DEFAULT_PERSON_LIST_FILTERS,
  normalizeValue: normalizePersonListFilters,
  isDefaultValue: isDefaultPersonListFilters,
  getActiveCount: getPersonListActiveFilterCount,
  loadCounts: loadPersonFacetCounts,
  renderButton: (controller) => <PersonListFacetButton controller={controller} />,
  renderDrawer: (controller) => <PersonListFacets controller={controller} />,
  renderChips: (controller) => <PersonListFacetChips controller={controller} />
};

export default PersonListFacets;
